/**
 * Models Compose modal — org-persisted size×style / top-down SKU plan,
 * match desk, commit stubs (collaborative drafts).
 */
import { useEffect, useState } from "react";
import {
  ghostBtnClass,
  mfgConfirmDialogClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useAuth } from "@/contexts/AuthContext";
import { applyArchetypeDraftToForm } from "@/lib/manufacturing/composePlan/coerceArchetypeDraft";
import { findNearestMatchedSibling } from "@/lib/manufacturing/composePlan/duplicateFromNearest";
import { extractComposeArchetype } from "@/lib/manufacturing/composeArchetypeClient";
import {
  createManufacturingBomAuthoringSession,
} from "@/lib/manufacturing/bomAuthoringSessionsClient";
import {
  getManufacturingRecipeDetail,
  setManufacturingRecipeLines,
} from "@/lib/manufacturing/recipesClient";
import {
  listManufacturingRecipeGroups,
  type MfgRecipeGroupSummary,
} from "@/lib/manufacturing/recipeGroupsClient";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingModelsComposeCommitStep } from "./ManufacturingModelsComposeCommitStep";
import { ManufacturingModelsComposeDefineForm } from "./ManufacturingModelsComposeDefineForm";
import { ManufacturingModelsComposeEntry } from "./ManufacturingModelsComposeEntry";
import { ManufacturingModelsComposeMatchDesk } from "./ManufacturingModelsComposeMatchDesk";
import { useManufacturingModelsComposePlan } from "./useManufacturingModelsComposePlan";

export function ManufacturingModelsComposeModal({
  recipes,
  initialGroupId,
  onClose,
  onDone,
  onRecipesRefresh,
  onOpenBomSandbox,
}: {
  recipes: readonly MfgRecipeSummary[];
  initialGroupId?: string;
  onClose: () => void;
  onDone: (message: string) => void;
  /** Refresh catalog without closing (after partial commit). */
  onRecipesRefresh?: () => void;
  onOpenBomSandbox?: (groupId?: string) => void;
}) {
  const auth = useAuth();
  const orgId = auth?.status === "ready" ? auth.activeOrganizationId ?? "local" : "local";
  const [groups, setGroups] = useState<MfgRecipeGroupSummary[]>([]);
  const [extractBusy, setExtractBusy] = useState(false);
  const [pendingSandboxCodes, setPendingSandboxCodes] = useState<string[]>([]);
  const [nearestHints, setNearestHints] = useState<Record<string, string>>({});

  const initialGroup = groups.find((g) => g.id === initialGroupId);

  const c = useManufacturingModelsComposePlan({
    orgId,
    recipes,
    initialGroupId,
    initialGroupCode: initialGroup?.code,
    initialGroupName: initialGroup?.name,
  });

  useEffect(() => {
    void listManufacturingRecipeGroups().then((r) => {
      if (r.ok) setGroups(r.data.groups);
    });
  }, []);

  async function handleExtractBrief() {
    if (!c.plan || !c.briefRaw.trim()) return;
    setExtractBusy(true);
    c.setError(null);
    const res = await extractComposeArchetype({
      transcript: c.briefRaw,
      size_axis_hint: c.plan.archetype.sizeAxisHint,
    });
    setExtractBusy(false);
    if (!res.ok) {
      c.setError(res.error);
      return;
    }
    c.patchArchetype(
      applyArchetypeDraftToForm(c.plan.archetype, res.data.draft, { overwrite: true }),
    );
    if (res.data.draft.styles.length) {
      c.setStylesRaw(res.data.draft.styles.join("\n"));
    }
  }

  async function handleCommit() {
    const result = await c.commitSelected();
    if (!result || !result.ok) return;

    for (const [nodeId, sourceRecipeId] of Object.entries(nearestHints)) {
      const targetId = result.result.recipeIdByNodeId[nodeId];
      if (!targetId || !sourceRecipeId) continue;
      const detail = await getManufacturingRecipeDetail(sourceRecipeId);
      if (!detail.ok) continue;
      const lines = detail.data.lines.map((l) => ({
        material_id: l.material_id ?? undefined,
        component_recipe_id: l.component_recipe_id ?? undefined,
        material_use_id: l.material_use_id ?? undefined,
        quantity: l.quantity,
        role: l.role,
        swappable: l.swappable,
        upgrade_price_delta: l.upgrade_price_delta,
        slot_label: l.slot_label || undefined,
      }));
      if (lines.length === 0) continue;
      await setManufacturingRecipeLines({ recipe_id: targetId, lines });
    }

    if (pendingSandboxCodes.length > 0 && onOpenBomSandbox) {
      const code = pendingSandboxCodes[0];
      const created = Object.entries(result.result.recipeIdByNodeId).find(([nid]) => {
        const node = c.plan?.nodes.find((n) => n.id === nid);
        return node?.code === code;
      });
      if (created) {
        await createManufacturingBomAuthoringSession({
          recipe_group_id: c.plan?.archetype.recipe_group_id ?? undefined,
          brief_text: `Deepen stub ${code}`,
          source: "typed",
        });
      }
      onOpenBomSandbox(c.plan?.archetype.recipe_group_id ?? undefined);
      onDone(result.message);
      return;
    }

    onRecipesRefresh?.();
    if (result.allDone) {
      await c.finishPlan();
      onDone(`${result.message} · series complete`);
      return;
    }
    // Stay open; unfinished products remain; committed ones are hidden.
    c.setStep("match");
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfg-compose-title"
    >
      <div
        className={`max-h-[90vh] w-full max-w-3xl overflow-y-auto ${mfgConfirmDialogClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3
              id="mfg-compose-title"
              className="text-base font-semibold text-gray-900 dark:text-zinc-100"
            >
              Compose products
            </h3>
            <p className="text-xs text-slate-500">
              Local draft (saved in this browser) · match some SKUs, commit, continue later
            </p>
          </div>
          <button type="button" className={ghostBtnClass} onClick={onClose} disabled={c.busy}>
            Close
          </button>
        </div>

        {c.error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {c.error}
          </div>
        ) : null}

        {!c.plan ? (
          <ManufacturingModelsComposeEntry
            savedPlans={c.savedPlans}
            busy={c.busy}
            onResume={(id) => void c.resume(id)}
            onDiscard={(id) => void c.discardSaved(id)}
            onStart={(mode) => void c.start(mode)}
            onImportJson={(raw) => {
              void c.importJson(raw).then((ok) => {
                if (ok) c.setStep("define");
              });
            }}
          />
        ) : null}

        {c.plan?.step === "define" ? (
          <div className="space-y-3">
            <ManufacturingModelsComposeDefineForm
              entryMode={c.plan.entryMode}
              archetype={c.plan.archetype}
              stylesRaw={c.stylesRaw}
              groups={groups}
              briefRaw={c.briefRaw}
              extractBusy={extractBusy}
              onStylesRaw={c.setStylesRaw}
              onBriefRaw={c.setBriefRaw}
              onPatch={c.patchArchetype}
              onExtractBrief={() => void handleExtractBrief()}
              onGenerateError={(msg) => c.setError(msg)}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={ghostBtnClass} onClick={() => c.setPlan(null)}>
                Back
              </button>
              <button type="button" className={mfgPrimaryBtnClass} onClick={c.expand}>
                Expand / seed SKUs
              </button>
            </div>
          </div>
        ) : null}

        {c.plan?.step === "review" ? (
          <div className="space-y-3">
            <p className="text-sm">
              {c.products.length} unfinished products
              {(c.stats?.committedProducts ?? 0) > 0
                ? ` · ${c.stats?.committedProducts} committed hidden`
                : ""}{" "}
              · {c.components.length} active components
            </p>
            <ul className="max-h-64 space-y-1 overflow-y-auto text-xs">
              {c.products.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1 dark:border-zinc-700"
                >
                  <span>
                    <span className="font-medium">{p.code}</span> · {p.name}
                  </span>
                  <button
                    type="button"
                    className={ghostBtnClass}
                    onClick={() => c.removeProduct(p.id)}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={ghostBtnClass} onClick={() => c.setStep("define")}>
                Back
              </button>
              <button
                type="button"
                className={ghostBtnClass}
                onClick={() => {
                  const blob = new Blob([c.exportJson()], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${c.plan?.planId ?? "compose"}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Export JSON
              </button>
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                onClick={() => c.setStep("match")}
              >
                Match components
              </button>
            </div>
          </div>
        ) : null}

        {c.plan?.step === "match" ? (
          <div className="space-y-3">
            <ManufacturingModelsComposeMatchDesk
              components={c.components}
              products={c.products}
              recipes={recipes}
              selectedNodeId={c.selectedNodeId}
              selectedNode={c.selectedNode}
              suggestions={c.suggestions}
              statusHelp={[
                "Showing unfinished work only",
                (c.stats?.committedProducts ?? 0) > 0
                  ? `(${c.stats?.committedProducts} committed hidden)`
                  : null,
                `Products: ${c.stats?.readyProducts ?? 0} ready · ${c.stats?.pendingProducts ?? 0} incomplete. Progress autosaves to the org.`,
              ]
                .filter(Boolean)
                .join(" ")}
              onSelect={c.setSelectedNodeId}
              onMatch={c.matchNode}
              onMatchMany={c.matchNodes}
              onReclassify={c.reclassify}
              onSplitByStyle={c.splitByStyle}
              onSplitByStyleMany={c.splitByStyleMany}
              onRefreshSuggestions={c.refreshSuggestions}
              onOpenBomSandbox={(node) => {
                setPendingSandboxCodes((prev) =>
                  prev.includes(node.code) ? prev : [...prev, node.code],
                );
              }}
              onDuplicateFromNearest={(node) => {
                const nearest = findNearestMatchedSibling(node, c.components);
                if (!nearest || nearest.match.status !== "matched") {
                  c.setError("No matched sibling of the same slot/size family.");
                  return;
                }
                const sourceId = nearest.match.recipe_id;
                setNearestHints((h) => ({
                  ...h,
                  [node.id]: sourceId,
                }));
                c.matchNode(node.id, null, true);
              }}
            />
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={ghostBtnClass} onClick={() => c.setStep("review")}>
                Back
              </button>
              <button
                type="button"
                className={mfgPrimaryBtnClass}
                disabled={(c.stats?.readyProducts ?? 0) === 0}
                onClick={() => c.setStep("commit")}
              >
                Review commit ({c.stats?.readyProducts ?? 0} ready)
              </button>
            </div>
          </div>
        ) : null}

        {c.plan?.step === "commit" ? (
          <ManufacturingModelsComposeCommitStep
            readyProducts={c.readyProducts}
            selectedCommitIds={c.selectedCommitIds}
            stubCount={c.order?.stubs.length ?? 0}
            selectedProductCount={c.order?.products.length ?? 0}
            commitLog={c.commitLog}
            pendingSandboxCodes={pendingSandboxCodes}
            busy={c.busy}
            onToggle={(productId, checked) => {
              c.setSelectedCommitIds((ids) =>
                checked ? [...ids, productId] : ids.filter((id) => id !== productId),
              );
            }}
            onBack={() => c.setStep("match")}
            onCommit={() => void handleCommit()}
          />
        ) : null}
      </div>
    </div>
  );
}
